import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaste, faCopy, faScissors, faPaintRoller,
  faBold, faItalic, faUnderline, faBorderAll, faFill, faA,
  faAlignLeft, faAlignCenter, faAlignRight, faIndent, faOutdent, faTextWidth, faObjectGroup,
  faAnglesUp, faAnglesDown, faGripLines, faRotate,
  faDollarSign, faPercent, faCalendarDay, faHashtag,
  faTableCellsLarge, faTable, faPalette,
  faPlus, faMinus, faGear,
  faCalculator, faArrowDownAZ, faArrowUpZA, faMagnifyingGlass, faEraser, faChartColumn, faDatabase,
  faImage, faShapes, faIcons, faCube, faCamera, faChartArea, faChartSimple, faChartLine, faChartPie,
  faMap, faFilter, faClock, faLink, faComment, faFont, faHeading, faSquareRootVariable, faSuperscript,
  faPen, faDrawPolygon, faSwatchbook, faRulerCombined, faExpand, faPrint, faBorderNone,
  faMoneyBill, faQuestion, faEllipsis, faTag, faSitemap, faTriangleExclamation, faEye, faPlay,
  faArrowsRotate, faPlug, faBolt, faClone, faCircleCheck, faLayerGroup, faChartBar,
  faSpellCheck, faBook, faUniversalAccess, faLanguage, faTrash, faLock, faUnlock,
  faTableCells, faList, faCompass, faRuler, faMagnifyingGlassPlus, faWindowRestore, faSnowflake, faEyeSlash,
  faCircleDot, faFileCode, faCode, faListCheck, faRobot,
} from "@fortawesome/free-solid-svg-icons";

const TABS = ["Home", "Insert", "Draw", "Page Layout", "Formulas", "Data", "Review", "View", "Automate"];
const FONTS = ["Default", "Calibri", "Aptos Narrow", "Arial", "Times New Roman", "Georgia", "Verdana", "Courier New", "Tahoma"];
const SIZES = [8, 9, 10, 11, 12, 13, 14, 16, 18, 20, 24, 28, 36];
const NUMFMTS = [["gen", "General"], ["num", "Number"], ["cur", "Currency"], ["acc", "Accounting"], ["pct", "Percentage"], ["comma", "Comma"], ["date", "Date"]];
const SYMBOLS = ["€", "£", "¥", "₹", "©", "®", "™", "°", "±", "×", "÷", "→", "✓", "★", "•", "§"];
const FN_GROUPS = {
  Financial: null,
  Logical: ["IF", "IFS", "AND", "OR", "NOT", "IFERROR", "IFNA"],
  Text: ["CONCAT", "TEXTJOIN", "LEFT", "RIGHT", "MID", "LEN", "UPPER", "LOWER", "PROPER", "TRIM", "SUBSTITUTE", "FIND"],
  "Date & Time": ["TODAY", "NOW", "DATE", "YEAR", "MONTH", "DAY", "WEEKDAY"],
  "Lookup & Reference": ["VLOOKUP", "HLOOKUP", "INDEX", "MATCH"],
  "Maths & Trig": ["SUM", "PRODUCT", "ROUND", "ABS", "SQRT", "POWER", "MOD", "INT", "CEILING", "FLOOR", "SIGN"],
  "More Functions": ["AVERAGE", "MEDIAN", "COUNT", "COUNTA", "MIN", "MAX", "SUMIF", "COUNTIF", "AVERAGEIF"],
};
const pd = (e) => e.preventDefault();

// ── primitives ────────────────────────────────────────────────────────────────
const Caret = () => <span className="xl-caret">▾</span>;

// Group box with a label underneath (Excel ribbon group).
const Group = ({ name, children }) => (
  <div className="xl-group">
    <div className="xl-group__body">{children}</div>
    <div className="xl-group__name">{name}</div>
  </div>
);
const Rows = ({ children }) => <div className="xl-rows">{children}</div>;
const Row = ({ children }) => <div className="xl-row">{children}</div>;
const Stack = ({ children }) => <div className="xl-cstack">{children}</div>;

// Small square icon button.
const Ico = ({ icon, on, title, onClick, children }) => (
  <button type="button" className={`xl-ico${on ? " is-on" : ""}`} title={title} onMouseDown={pd} onClick={onClick}>
    {icon ? <FontAwesomeIcon icon={icon} /> : children}
  </button>
);
// Big icon-over-label button (tall).
const Big = ({ icon, label, on, title, onClick }) => (
  <button type="button" className={`xl-big${on ? " is-on" : ""}`} title={title} onMouseDown={pd} onClick={onClick}>
    <FontAwesomeIcon icon={icon} className="xl-big__ico" />
    <span className="xl-big__lbl">{label}</span>
  </button>
);
// Adaptive: labelled → Big, otherwise small Ico.
const Btn = (p) => (p.label ? <Big {...p} /> : <Ico {...p} />);
// Horizontal small button (icon + label [+ caret]) used in stacked groups (Cells/Editing).
const RowBtn = ({ icon, label, on, title, onClick, caret }) => (
  <button type="button" className={`xl-rowbtn${on ? " is-on" : ""}`} title={title} onMouseDown={pd} onClick={onClick}>
    <FontAwesomeIcon icon={icon} /> <span className="xl-rowbtn__lbl">{label}</span>{caret && <Caret />}
  </button>
);
// Color split button: glyph + colour bar + opens the native picker.
const Color = ({ icon, value, onChange, title }) => (
  <label className="xl-color" title={title}>
    <FontAwesomeIcon icon={icon} />
    <span className="xl-color__bar" style={{ background: value }} />
    <input type="color" value={value} onChange={(e) => onChange(e.target.value)} />
  </label>
);
const MItem = ({ icon, onClick, children }) => (
  <button type="button" className="xl-mitem" onMouseDown={pd} onClick={onClick}>
    {icon && <FontAwesomeIcon icon={icon} />} {children}
  </button>
);

// Dropdown menu. `variant`: 'big' (tall icon+label+caret), 'ico' (small square+caret),
// 'row' (horizontal icon+label+caret).
const Menu = ({ label, icon, title, children, wide, variant }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);
  const kind = variant || (label ? "big" : "ico");
  return (
    <span className={`xl-menuwrap${kind === "big" ? " xl-menuwrap--big" : ""}`} ref={ref}>
      {kind === "big" ? (
        <button type="button" className="xl-big xl-big--menu" title={title} onMouseDown={pd} onClick={() => setOpen((v) => !v)}>
          <FontAwesomeIcon icon={icon} className="xl-big__ico" />
          <span className="xl-big__lbl">{label} <Caret /></span>
        </button>
      ) : kind === "row" ? (
        <button type="button" className="xl-rowbtn" title={title} onMouseDown={pd} onClick={() => setOpen((v) => !v)}>
          <FontAwesomeIcon icon={icon} /> <span className="xl-rowbtn__lbl">{label}</span><Caret />
        </button>
      ) : (
        <button type="button" className="xl-ico xl-ico--menu" title={title} onMouseDown={pd} onClick={() => setOpen((v) => !v)}>
          <FontAwesomeIcon icon={icon} /><Caret />
        </button>
      )}
      {open && <div className={`xl-menu${wide ? " xl-menu--wide" : ""}`} onClick={() => setOpen(false)}>{children}</div>}
    </span>
  );
};

/**
 * SheetRibbon — Excel-style tabbed ribbon. Home is fully wired to the grid via
 * `api`; other tabs surface real features where feasible and stub the rest
 * (api.notAvail → toast). Layout mirrors Excel: grouped, labelled, two-row.
 */
const SheetRibbon = ({ api, tab, onTab }) => {
  const f = api.fmt || {};
  const nfType = f.nf ? f.nf.t : "gen";

  return (
    <div className="xl-ribbon">
      <div className="xl-tabs">
        {TABS.map((t) => (
          <button key={t} type="button" className={`xl-tab${tab === t ? " is-active" : ""}`} onClick={() => onTab(t)}>{t}</button>
        ))}
      </div>

      {tab === "Home" && (
        <div className="xl-bar">
          <Group name="Clipboard">
            <Menu icon={faPaste} title="Paste (Ctrl+V)" label="Paste">
              <MItem icon={faPaste} onClick={api.paste}>Paste (Ctrl+V)</MItem>
            </Menu>
            <Stack>
              <Ico icon={faScissors} title="Cut (Ctrl+X)" onClick={api.cut} />
              <Ico icon={faCopy} title="Copy (Ctrl+C)" onClick={api.copy} />
              <Ico icon={faPaintRoller} on={api.painter} title="Format Painter" onClick={api.startPainter} />
            </Stack>
          </Group>

          <Group name="Font">
            <Rows>
              <Row>
                <select className="xl-font" title="Font" value={f.ff || "Default"} onChange={(e) => api.fontFamily(e.target.value === "Default" ? "" : e.target.value)}>
                  {FONTS.map((ft) => <option key={ft} value={ft}>{ft}</option>)}
                </select>
                <select className="xl-size" title="Font size" value={f.fs || 13} onChange={(e) => api.fontSize(Number(e.target.value))}>
                  {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <Ico title="Grow font" onClick={api.fontGrow}><span className="xl-abig">A↑</span></Ico>
                <Ico title="Shrink font" onClick={api.fontShrink}><span className="xl-asml">A↓</span></Ico>
              </Row>
              <Row>
                <Ico icon={faBold} on={f.b} title="Bold (Ctrl+B)" onClick={api.bold} />
                <Ico icon={faItalic} on={f.i} title="Italic (Ctrl+I)" onClick={api.italic} />
                <Ico icon={faUnderline} on={f.u} title="Underline (Ctrl+U)" onClick={api.underline} />
                <Menu icon={faBorderAll} title="Borders">
                  <MItem icon={faBorderAll} onClick={() => api.borders("all")}>All Borders</MItem>
                  <MItem onClick={() => api.borders("outer")}>Outside Borders</MItem>
                  <MItem onClick={() => api.borders("bottom")}>Bottom Border</MItem>
                  <MItem onClick={() => api.borders("top")}>Top Border</MItem>
                  <MItem onClick={() => api.borders("none")}>No Border</MItem>
                </Menu>
                <Color icon={faFill} value={f.bg || "#ffffff"} onChange={api.fillColor} title="Fill Color" />
                <Color icon={faA} value={f.fg || "#1f2733"} onChange={api.fontColor} title="Font Color" />
              </Row>
            </Rows>
          </Group>

          <Group name="Alignment">
            <Rows>
              <Row>
                <Ico icon={faAnglesUp} on={f.va === "top"} title="Top Align" onClick={() => api.vAlign("top")} />
                <Ico icon={faGripLines} on={f.va === "middle"} title="Middle Align" onClick={() => api.vAlign("middle")} />
                <Ico icon={faAnglesDown} on={!f.va || f.va === "bottom"} title="Bottom Align" onClick={() => api.vAlign("bottom")} />
                <Ico icon={faRotate} title="Orientation" onClick={() => api.notAvail("Orientation")} />
                <RowBtn icon={faTextWidth} on={f.wrap} title="Wrap Text" label="Wrap Text" onClick={api.wrap} />
              </Row>
              <Row>
                <Ico icon={faAlignLeft} on={(f.a || "left") === "left"} title="Align Left" onClick={() => api.hAlign("left")} />
                <Ico icon={faAlignCenter} on={f.a === "center"} title="Center" onClick={() => api.hAlign("center")} />
                <Ico icon={faAlignRight} on={f.a === "right"} title="Align Right" onClick={() => api.hAlign("right")} />
                <Ico icon={faOutdent} title="Decrease Indent" onClick={() => api.indent(-1)} />
                <Ico icon={faIndent} title="Increase Indent" onClick={() => api.indent(1)} />
                <RowBtn icon={faObjectGroup} title="Merge & Center" label="Merge & Centre" onClick={api.merge} />
              </Row>
            </Rows>
          </Group>

          <Group name="Number">
            <Rows>
              <Row>
                <select className="xl-numfmt" title="Number Format" value={nfType} onChange={(e) => api.numFmt(e.target.value)}>
                  {NUMFMTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </Row>
              <Row>
                <Ico icon={faDollarSign} on={nfType === "acc"} title="Accounting" onClick={api.accounting} />
                <Ico icon={faPercent} on={nfType === "pct"} title="Percent" onClick={api.percent} />
                <Ico icon={faHashtag} on={nfType === "comma"} title="Comma Style" onClick={api.comma} />
                <Ico icon={faCalendarDay} on={nfType === "date"} title="Date" onClick={() => api.numFmt("date")} />
                <Ico title="Increase Decimal" onClick={api.incDec}><span className="xl-tiny">.0→</span></Ico>
                <Ico title="Decrease Decimal" onClick={api.decDec}><span className="xl-tiny">←.0</span></Ico>
              </Row>
            </Rows>
          </Group>

          <Group name="Styles">
            <Menu icon={faPalette} title="Conditional Formatting" label="Conditional" wide>
              <MItem onClick={() => api.condRule("gt")}>Highlight: Greater Than…</MItem>
              <MItem onClick={() => api.condRule("lt")}>Highlight: Less Than…</MItem>
              <MItem onClick={() => api.condRule("eq")}>Highlight: Equal To…</MItem>
              <MItem onClick={() => api.condRule("contains")}>Highlight: Text Contains…</MItem>
              <MItem icon={faEraser} onClick={api.clearCond}>Clear Rules (sheet)</MItem>
            </Menu>
            <Big icon={faTable} title="Format as Table" label="Format as Table" onClick={api.formatAsTable} />
            <Menu icon={faTableCellsLarge} title="Cell Styles" label="Cell Styles" wide>
              {api.cellStyleNames.map((n) => <MItem key={n} onClick={() => api.cellStyle(n)}>{n}</MItem>)}
            </Menu>
          </Group>

          <Group name="Cells">
            <Stack>
              <Menu icon={faPlus} title="Insert" label="Insert" variant="row">
                <MItem onClick={api.insertRow}>Insert Sheet Row</MItem>
                <MItem onClick={api.insertCol}>Insert Sheet Column</MItem>
              </Menu>
              <Menu icon={faMinus} title="Delete" label="Delete" variant="row">
                <MItem onClick={api.deleteRow}>Delete Sheet Row</MItem>
                <MItem onClick={api.deleteCol}>Delete Sheet Column</MItem>
              </Menu>
              <Menu icon={faGear} title="Format" label="Format" variant="row">
                <MItem onClick={api.rowHeight}>Row Height…</MItem>
                <MItem onClick={api.colWidth}>Column Width…</MItem>
              </Menu>
            </Stack>
          </Group>

          <Group name="Editing">
            <Stack>
              <RowBtn icon={faCalculator} title="AutoSum" label="AutoSum" onClick={api.autoSum} />
              <Menu icon={faArrowDownAZ} title="Sort & Filter" label="Sort & Filter" variant="row">
                <MItem icon={faArrowDownAZ} onClick={api.sortAsc}>Sort A → Z</MItem>
                <MItem icon={faArrowUpZA} onClick={api.sortDesc}>Sort Z → A</MItem>
              </Menu>
              <RowBtn icon={faMagnifyingGlass} title="Find & Replace (Ctrl+F)" label="Find &amp; Select" onClick={api.find} />
            </Stack>
          </Group>
        </div>
      )}

      {tab === "Insert" && (
        <div className="xl-bar">
          <Group name="Tables">
            <Btn icon={faTable} title="PivotTable (not available yet)" onClick={() => api.notAvail("PivotTable")} label="PivotTable" />
            <Btn icon={faTable} title="Recommended PivotTables" onClick={() => api.notAvail("Recommended PivotTables")} label="Recommended" />
            <Btn icon={faTable} title="Format as Table" onClick={api.formatAsTable} label="Table" />
          </Group>
          <Group name="Illustrations">
            <Btn icon={faImage} title="Pictures" onClick={() => api.notAvail("Pictures")} label="Pictures" />
            <Btn icon={faShapes} title="Shapes" onClick={() => api.notAvail("Shapes")} label="Shapes" />
            <Btn icon={faIcons} title="Icons" onClick={() => api.notAvail("Icons")} label="Icons" />
            <Stack>
              <Ico icon={faCube} title="3D Models" onClick={() => api.notAvail("3D Models")} />
              <Ico icon={faSitemap} title="SmartArt" onClick={() => api.notAvail("SmartArt")} />
              <Ico icon={faCamera} title="Screenshot" onClick={() => api.notAvail("Screenshot")} />
            </Stack>
          </Group>
          <Group name="Charts">
            <Btn icon={faChartSimple} title="Recommended Charts" onClick={api.insertChart} label="Recommended" />
            <Stack>
              <Ico icon={faChartColumn} title="Column chart" onClick={api.insertChart} />
              <Ico icon={faChartLine} title="Line chart" onClick={api.insertChart} />
              <Ico icon={faChartPie} title="Pie chart" onClick={api.insertChart} />
            </Stack>
            <Btn icon={faChartArea} title="PivotChart" onClick={api.insertChart} label="PivotChart" />
            <Btn icon={faMap} title="Maps" onClick={() => api.notAvail("Maps")} label="Maps" />
          </Group>
          <Group name="Sparklines">
            <Btn icon={faChartLine} title="Sparklines" onClick={() => api.notAvail("Sparklines")} label="Sparklines" />
          </Group>
          <Group name="Filters">
            <Btn icon={faFilter} title="Slicer" onClick={() => api.notAvail("Slicer")} label="Slicer" />
            <Btn icon={faClock} title="Timeline" onClick={() => api.notAvail("Timeline")} label="Timeline" />
          </Group>
          <Group name="Links">
            <Btn icon={faLink} title="Insert Link" onClick={api.insertLink} label="Link" />
          </Group>
          <Group name="Comments">
            <Btn icon={faComment} title="New Comment" onClick={api.addComment} label="Comment" />
          </Group>
          <Group name="Text">
            <Btn icon={faFont} title="Text Box" onClick={() => api.notAvail("Text Box")} label="Text Box" />
            <Btn icon={faHeading} title="Header & Footer" onClick={() => api.notAvail("Header & Footer")} label="Header" />
            <Btn icon={faFont} title="WordArt" onClick={() => api.notAvail("WordArt")} label="WordArt" />
          </Group>
          <Group name="Symbols">
            <Btn icon={faSquareRootVariable} title="Equation" onClick={() => api.notAvail("Equation")} label="Equation" />
            <Menu icon={faSuperscript} title="Symbol" label="Symbol">
              <div className="xl-symgrid">
                {SYMBOLS.map((s) => <button key={s} type="button" className="xl-sym" onMouseDown={pd} onClick={() => api.insertSymbol(s)}>{s}</button>)}
              </div>
            </Menu>
          </Group>
        </div>
      )}

      {tab === "Draw" && (
        <div className="xl-bar">
          <Group name="Drawing Tools">
            <Btn icon={faPen} title="Draw" onClick={() => api.notAvail("Draw")} label="Draw" />
            <Btn icon={faEraser} title="Eraser" onClick={() => api.notAvail("Eraser")} label="Eraser" />
            <Btn icon={faDrawPolygon} title="Lasso Select" onClick={() => api.notAvail("Lasso Select")} label="Lasso" />
          </Group>
          <div className="xl-soon">Ink drawing needs a canvas layer — not available in BluePlanit Sheets.</div>
        </div>
      )}

      {tab === "Page Layout" && (
        <div className="xl-bar">
          <Group name="Themes">
            <Btn icon={faSwatchbook} title="Themes" onClick={() => api.notAvail("Themes")} label="Themes" />
            <Btn icon={faPalette} title="Colours" onClick={() => api.notAvail("Colours")} label="Colours" />
            <Btn icon={faFont} title="Fonts" onClick={() => api.notAvail("Fonts")} label="Fonts" />
          </Group>
          <Group name="Page Setup">
            <Btn icon={faRulerCombined} title="Margins" onClick={() => api.notAvail("Margins")} label="Margins" />
            <Btn icon={faExpand} title="Orientation" onClick={() => api.notAvail("Orientation")} label="Orientation" />
            <Btn icon={faExpand} title="Size" onClick={() => api.notAvail("Size")} label="Size" />
            <Btn icon={faPrint} title="Print Area" onClick={() => api.notAvail("Print Area")} label="Print Area" />
          </Group>
          <Group name="Sheet Options">
            <Btn icon={faBorderNone} on={api.view.gridlines} title="Gridlines: View" onClick={api.toggleGridlines} label="Gridlines" />
            <Btn icon={faHeading} on={api.view.headings} title="Headings: View" onClick={api.toggleHeadings} label="Headings" />
          </Group>
        </div>
      )}

      {tab === "Formulas" && (
        <div className="xl-bar">
          <Group name="Function Library">
            <Menu icon={faSquareRootVariable} title="Insert Function" label="Insert Fn" wide>
              {["SUM", "AVERAGE", "IF", "VLOOKUP", "COUNTIF", "CONCAT", "TODAY"].map((fn) => <MItem key={fn} onClick={() => api.insertFunction(fn)}>{fn}</MItem>)}
            </Menu>
            <Btn icon={faCalculator} title="AutoSum" onClick={api.autoSum} label="AutoSum" />
            {Object.entries(FN_GROUPS).map(([grp, fns]) => (
              <Menu key={grp} icon={grp === "Financial" ? faMoneyBill : grp === "Logical" ? faQuestion : grp === "Text" ? faFont : grp.startsWith("Date") ? faClock : grp.startsWith("Lookup") ? faMagnifyingGlass : grp.startsWith("Maths") ? faSquareRootVariable : faEllipsis} title={grp} label={grp.replace(" & Reference", "").replace(" & Trig", "").replace(" Functions", "")} wide>
                {fns ? fns.map((fn) => <MItem key={fn} onClick={() => api.insertFunction(fn)}>{fn}</MItem>) : <MItem onClick={() => api.notAvail("Financial functions")}>Not available yet</MItem>}
              </Menu>
            ))}
          </Group>
          <Group name="Defined Names">
            <Btn icon={faTag} title="Name Manager" onClick={() => api.notAvail("Name Manager")} label="Names" />
          </Group>
          <Group name="Formula Auditing">
            <Btn icon={faSitemap} title="Trace Precedents" onClick={() => api.notAvail("Trace Precedents")} label="Trace" />
            <Btn icon={faTriangleExclamation} title="Error Checking" onClick={() => api.notAvail("Error Checking")} label="Errors" />
            <Btn icon={faEye} on={api.view.showFormulas} title="Show Formulas" onClick={api.toggleShowFormulas} label="Show Formulas" />
          </Group>
          <Group name="Calculation">
            <Btn icon={faPlay} title="Calculate Now (auto)" onClick={() => api.notAvail("Manual calculation")} label="Calc Now" />
          </Group>
        </div>
      )}

      {tab === "Data" && (
        <div className="xl-bar">
          <Group name="Get & Transform (live)">
            <Menu icon={faDatabase} title="Get live data from your dashboard" label="Get Data" wide>
              {Object.entries(api.datasets).map(([k, ds]) => <MItem key={k} icon={faDatabase} onClick={() => api.importData(k)}>{ds.label}</MItem>)}
            </Menu>
            <Btn icon={faArrowsRotate} title="Refresh All" onClick={() => api.notAvail("Refresh All")} label="Refresh" />
            <Btn icon={faPlug} title="Queries & Connections" onClick={() => api.notAvail("Queries & Connections")} label="Queries" />
          </Group>
          <Group name="Sort & Filter">
            <Btn icon={faArrowDownAZ} title="Sort A→Z" onClick={api.sortAsc} label="A→Z" />
            <Btn icon={faArrowUpZA} title="Sort Z→A" onClick={api.sortDesc} label="Z→A" />
            <Btn icon={faFilter} title="Filter" onClick={() => api.notAvail("Filter")} label="Filter" />
          </Group>
          <Group name="Data Tools">
            <Btn icon={faTableCells} title="Text to Columns" onClick={api.textToColumns} label="Text→Cols" />
            <Btn icon={faBolt} title="Flash Fill" onClick={() => api.notAvail("Flash Fill")} label="Flash Fill" />
            <Btn icon={faClone} title="Remove Duplicates" onClick={api.removeDuplicates} label="Dedupe" />
            <Btn icon={faCircleCheck} title="Data Validation" onClick={() => api.notAvail("Data Validation")} label="Validation" />
          </Group>
          <Group name="Outline">
            <Btn icon={faLayerGroup} title="Group" onClick={() => api.notAvail("Group")} label="Group" />
            <Btn icon={faLayerGroup} title="Subtotal" onClick={() => api.notAvail("Subtotal")} label="Subtotal" />
          </Group>
          <Group name="Analysis">
            <Btn icon={faChartBar} title="Analysis Tools" onClick={() => api.notAvail("Analysis ToolPak")} label="Analysis" />
          </Group>
        </div>
      )}

      {tab === "Review" && (
        <div className="xl-bar">
          <Group name="Proofing">
            <Btn icon={faSpellCheck} title="Spelling" onClick={() => api.notAvail("Spelling")} label="Spelling" />
            <Btn icon={faBook} title="Thesaurus" onClick={() => api.notAvail("Thesaurus")} label="Thesaurus" />
            <Btn icon={faChartBar} title="Workbook Statistics" onClick={() => api.notAvail("Workbook Statistics")} label="Stats" />
          </Group>
          <Group name="Accessibility">
            <Btn icon={faUniversalAccess} title="Check Accessibility" onClick={() => api.notAvail("Check Accessibility")} label="Access" />
          </Group>
          <Group name="Insights">
            <Btn icon={faMagnifyingGlass} title="Smart Lookup" onClick={() => api.notAvail("Smart Lookup")} label="Lookup" />
            <Btn icon={faLanguage} title="Translate" onClick={() => api.notAvail("Translate")} label="Translate" />
          </Group>
          <Group name="Comments">
            <Btn icon={faComment} title="New Comment" onClick={api.addComment} label="Comment" />
            <Btn icon={faTrash} title="Delete Comment" onClick={api.addComment} label="Delete" />
            <Btn icon={faComment} title="Notes" onClick={api.addComment} label="Notes" />
          </Group>
          <Group name="Protect">
            <Btn icon={api.protectedSheet ? faUnlock : faLock} on={api.protectedSheet} title={api.protectedSheet ? "Unprotect Sheet" : "Protect Sheet"} onClick={api.toggleProtect} label={api.protectedSheet ? "Unprotect" : "Protect Sheet"} />
            <Btn icon={faLock} title="Protect Workbook" onClick={() => api.notAvail("Protect Workbook")} label="Workbook" />
          </Group>
        </div>
      )}

      {tab === "View" && (
        <div className="xl-bar">
          <Group name="Workbook Views">
            <Btn icon={faTableCells} on title="Normal" onClick={() => {}} label="Normal" />
            <Btn icon={faList} title="Page Break Preview" onClick={() => api.notAvail("Page Break Preview")} label="Page Break" />
            <Btn icon={faCompass} title="Custom Views" onClick={() => api.notAvail("Custom Views")} label="Custom" />
          </Group>
          <Group name="Show">
            <Stack>
              <RowBtn icon={faRuler} title="Ruler" label="Ruler" onClick={() => api.notAvail("Ruler")} />
              <RowBtn icon={faBorderNone} on={api.view.gridlines} title="Gridlines" label="Gridlines" onClick={api.toggleGridlines} />
              <RowBtn icon={faSuperscript} on={api.view.formulaBar} title="Formula Bar" label="Formula Bar" onClick={api.toggleFormulaBar} />
              <RowBtn icon={faHeading} on={api.view.headings} title="Headings" label="Headings" onClick={api.toggleHeadings} />
            </Stack>
          </Group>
          <Group name="Zoom">
            <Btn icon={faMagnifyingGlassPlus} title="Zoom In" onClick={() => api.setZoom((api.view.zoom || 100) + 10)} label={`${api.view.zoom || 100}%`} />
            <Stack>
              <RowBtn icon={faMagnifyingGlassPlus} title="Zoom In" label="Zoom In" onClick={() => api.setZoom((api.view.zoom || 100) + 10)} />
              <RowBtn icon={faMagnifyingGlass} title="Zoom Out" label="Zoom Out" onClick={() => api.setZoom((api.view.zoom || 100) - 10)} />
              <RowBtn icon={faExpand} title="Zoom to 100%" label="100%" onClick={() => api.setZoom(100)} />
            </Stack>
          </Group>
          <Group name="Window">
            <Btn icon={faWindowRestore} title="New Window" onClick={() => api.notAvail("New Window")} label="New Window" />
            <Menu icon={faSnowflake} title="Freeze Panes" label="Freeze Panes" wide>
              <MItem icon={faSnowflake} onClick={() => api.setFreeze("panes")}>Freeze Panes (at selection)</MItem>
              <MItem onClick={() => api.setFreeze("topRow")}>Freeze Top Row</MItem>
              <MItem onClick={() => api.setFreeze("firstCol")}>Freeze First Column</MItem>
              <MItem icon={faEyeSlash} onClick={() => api.setFreeze("none")}>Unfreeze Panes</MItem>
            </Menu>
            <Btn icon={faList} title="Switch Windows" onClick={() => api.notAvail("Switch Windows")} label="Switch" />
          </Group>
          <Group name="Macros">
            <Btn icon={faPlay} title="Macros" onClick={() => api.notAvail("Macros")} label="Macros" />
          </Group>
        </div>
      )}

      {tab === "Automate" && (
        <div className="xl-bar">
          <Group name="Scripting">
            <Btn icon={faCircleDot} title="Record Actions" onClick={() => api.notAvail("Record Actions")} label="Record" />
            <Btn icon={faFileCode} title="New Script" onClick={() => api.notAvail("New Script")} label="New Script" />
            <Btn icon={faCode} title="Office Scripts" onClick={() => api.notAvail("Office Scripts")} label="Scripts" />
            <Btn icon={faListCheck} title="All Scripts" onClick={() => api.notAvail("All Scripts")} label="All" />
          </Group>
          <Group name="Power Automate">
            <Btn icon={faRobot} title="Automate a Task" onClick={() => api.notAvail("Automate a Task")} label="Automate" />
          </Group>
        </div>
      )}
    </div>
  );
};

export default SheetRibbon;
